import { useCallback, useRef, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';

import { getQuotes, quoteFulfillmentPlan, saveConfiguration } from '../../api/ltl.api';
import { type FreightBookingFormState, WIZARD_MODE } from '../../components/freight-booking-wizard';
import {
  buildSaveConfigurationPayload,
  mapAddressToBackend,
  mapDimensionUnit,
  mapHandlingUnitsToPayload,
  mapWeightUnit,
  mapWizardHandlingCharge,
  mapWizardInsurance,
  mapWizardServicesToAccessorials,
} from '../freight-payload-mappers';

import type { WizardMode } from '../../components/freight-booking-wizard/freight-booking-wizard-types';
import type { BulkLoadResponse } from '../../types/bulkload-response';
import type { AsyncTaskErrorEnvelope } from '../../types/common';
import type { DimensionUnit } from '../../types/dimensions';
import type { FreightQuoteRequest } from '../../types/freight-requests';
import type { FulfillmentPlan } from '../../types/fulfillment-plan';
import type { LtlQuote } from '../../types/ltl';
import type { BaseWeightUnit } from '../../types/weight';
import type { AxiosError } from 'axios';

export type UseHandleBookingFormChange = {
  mode: WizardMode;
  fulfillmentPlanIdRef: MutableRefObject<string | null>;
  getConnectedProviderId: () => number | undefined;
  setIsLoadingRates: Dispatch<SetStateAction<boolean>>;
  setRateError: Dispatch<SetStateAction<string | undefined>>;
  setRateErrorCode: Dispatch<SetStateAction<string | null | undefined>>;
  setRates: Dispatch<SetStateAction<LtlQuote[]>>;
  ratesRef: MutableRefObject<LtlQuote[]>;
  priorSelectedRateRef: MutableRefObject<LtlQuote | null>;
  selectedRateRef: MutableRefObject<LtlQuote | null>;
  cancelQuoteListening: () => void;
  mountedRef: MutableRefObject<boolean>;
  startQuoteListening: (guids: string[]) => void;
  getFulfillmentPlan: () => FulfillmentPlan | undefined;
  weightUnit: BaseWeightUnit;
  dimensionUnit: DimensionUnit;
  formStateRef: MutableRefObject<FreightBookingFormState>;
  onSaveConfigurationSuccess: (result: BulkLoadResponse) => void;
  cancelFulfillmentPlanQuoteListening: () => void;
  startFulfillmentPlanQuoteListening: (guids: string[]) => void;
  onQuoteSuccess: (envelope: AsyncTaskErrorEnvelope, jobGuids: string[]) => void;
  onQuoteError: (err: AxiosError) => void;
  onQuoteFulfillmentPlanSuccess: (envelope: AsyncTaskErrorEnvelope, jobGuids: string[]) => void;
  onQuoteFulfillmentPlanError: (err: AxiosError) => void;
};

const buildQuoteRequest = (
  sellerFreightProviderId: number | null | undefined,
  formStateRef: MutableRefObject<FreightBookingFormState>,
  weightUnit: BaseWeightUnit,
  dimensionUnit: DimensionUnit,
): FreightQuoteRequest => {
  const { current } = formStateRef;
  return {
    sellerFreightProviderId,
    shipment: {
      shipFromId: Number(current.selectedShipFromId),
      destinationAddress: mapAddressToBackend(current.destinationAddress, current.deliveryServices.isLimitedAccess) ?? {},
      shipmentDate: current.shipmentDate ? `${current.shipmentDate.split('T')[0]}T09:00:00` : '',
      handlingUnits: mapHandlingUnitsToPayload(current.handlingUnits, mapWeightUnit(weightUnit), mapDimensionUnit(dimensionUnit)),
      additionalServices: mapWizardServicesToAccessorials(
        current.pickupServices,
        current.deliveryServices,
        current.shipmentServices,
        current.tradeshow,
        current.tradeshowPickup,
      ),
      insurance: mapWizardInsurance(current.insurance),
      handlingCharge: mapWizardHandlingCharge(current.handlingCharge),
    },
  };
};

// Edit this object to add or remove fields that trigger a re-quote.
const extractRequoteSnapshot = (state: FreightBookingFormState) => ({
  selectedShipFromId: state.selectedShipFromId,
  destinationCity: state.destinationAddress.city,
  destinationState: state.destinationAddress.state,
  destinationZip: state.destinationAddress.postalCode,
  handlingUnits: state.handlingUnits.map((u) => ({
    length: u.length,
    width: u.width,
    height: u.height,
    isStackable: u.isStackable,
    commodities: u.commodities.map((c) => ({
      weight: c.weight,
      freightClass: c.freightClass,
    })),
  })),
  isInsured: state.insurance.isInsured,
  insuranceValue: state.insurance.insuredValue,
  pickupServices: state.pickupServices,
  deliveryServices: state.deliveryServices,
});

const isFormReadyToQuote = (state: FreightBookingFormState): boolean => {
  if (!state.selectedShipFromId) return false;
  if (!state.destinationAddress.city || !state.destinationAddress.state || !state.destinationAddress.postalCode) return false;
  if (state.handlingUnits.length === 0) return false;
  return state.handlingUnits.every(
    (hu) =>
      hu.length > 0 &&
      hu.width > 0 &&
      hu.height > 0 &&
      hu.commodities.length > 0 &&
      hu.commodities.every((c) => c.weight > 0 && c.freightClass !== ''),
  );
};

export const useHandleBookingFormChange = ({
  mode,
  fulfillmentPlanIdRef,
  getConnectedProviderId,
  setIsLoadingRates,
  setRateError,
  setRateErrorCode,
  setRates,
  ratesRef,
  priorSelectedRateRef,
  selectedRateRef,
  cancelQuoteListening,
  mountedRef,
  startQuoteListening,
  getFulfillmentPlan,
  weightUnit,
  dimensionUnit,
  formStateRef,
  onSaveConfigurationSuccess,
  cancelFulfillmentPlanQuoteListening,
  startFulfillmentPlanQuoteListening,
  onQuoteSuccess,
  onQuoteError,
  onQuoteFulfillmentPlanSuccess,
  onQuoteFulfillmentPlanError,
}: UseHandleBookingFormChange) => {
  const requoteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requoteSnapshotRef = useRef<string>(JSON.stringify(extractRequoteSnapshot(formStateRef.current)));

  // Fires a brand-new quote request (no fulfillment plan exists yet).
  const fireNewQuote = useCallback(
    (sellerFreightProviderId?: number | null) => {
      setIsLoadingRates(true);
      setRateError(undefined);
      setRateErrorCode(undefined);
      setRates([]);
      // eslint-disable-next-line no-param-reassign
      ratesRef.current = [];
      // eslint-disable-next-line no-param-reassign
      priorSelectedRateRef.current = selectedRateRef.current;
      cancelQuoteListening();

      getQuotes(buildQuoteRequest(sellerFreightProviderId, formStateRef, weightUnit, dimensionUnit))
        .then((envelope) => {
          const jobGuids: string[] = (envelope.jobGuids ?? envelope.JobGuids ?? []) as string[];
          onQuoteSuccess(envelope, jobGuids);
          if (!mountedRef.current || !jobGuids.length) return;

          startQuoteListening(jobGuids);
        })
        .catch((err) => {
          onQuoteError(err);
        });
    },
    [
      setIsLoadingRates,
      setRateError,
      setRateErrorCode,
      setRates,
      ratesRef,
      priorSelectedRateRef,
      selectedRateRef,
      cancelQuoteListening,
      formStateRef,
      weightUnit,
      dimensionUnit,
      mountedRef,
      onQuoteSuccess,
      startQuoteListening,
      onQuoteError,
    ],
  );

  // Fires a re-quote against an existing fulfillment plan.
  const fireFulfillmentPlanQuote = useCallback(
    (fulfillmentPlanId: string) => {
      setIsLoadingRates(true);
      setRateError(undefined);
      setRateErrorCode(undefined);
      setRates([]);
      // eslint-disable-next-line no-param-reassign
      ratesRef.current = [];
      // eslint-disable-next-line no-param-reassign
      priorSelectedRateRef.current = selectedRateRef.current;
      cancelFulfillmentPlanQuoteListening();
      quoteFulfillmentPlan(fulfillmentPlanId)
        .then((envelope) => {
          const jobGuids: string[] = (envelope.jobGuids ?? envelope.JobGuids ?? []) as string[];
          onQuoteFulfillmentPlanSuccess(envelope, jobGuids);
          if (!mountedRef.current || !jobGuids.length) return;
          startFulfillmentPlanQuoteListening(jobGuids);
        })
        .catch((err) => {
          onQuoteFulfillmentPlanError(err);
        });
    },
    [
      setIsLoadingRates,
      setRateError,
      setRateErrorCode,
      setRates,
      ratesRef,
      priorSelectedRateRef,
      selectedRateRef,
      cancelFulfillmentPlanQuoteListening,
      mountedRef,
      onQuoteFulfillmentPlanSuccess,
      startFulfillmentPlanQuoteListening,
      onQuoteFulfillmentPlanError,
    ],
  );

  // Imperative re-quote, bypassing the debounce/snapshot-diff below — used right after
  // WWEX registration succeeds mid-booking.
  const triggerRequote = useCallback(
    (overrideSellerFreightProviderId?: number | null) => {
      const fulfillmentPlanId = fulfillmentPlanIdRef.current;
      if (fulfillmentPlanId) {
        fireFulfillmentPlanQuote(fulfillmentPlanId);
        return;
      }
      fireNewQuote(overrideSellerFreightProviderId ?? getConnectedProviderId());
    },
    [fulfillmentPlanIdRef, fireFulfillmentPlanQuote, fireNewQuote, getConnectedProviderId],
  );

  const onChange = useCallback(
    (next: FreightBookingFormState) => {
      if (mode === WIZARD_MODE.VIEW_BOOKED) return;

      const fulfillmentPlanIdForQuote = fulfillmentPlanIdRef.current;

      if (!fulfillmentPlanIdForQuote) {
        const sellerFreightProviderId = getConnectedProviderId();
        if (isFormReadyToQuote(next)) {
          if (requoteTimerRef.current) clearTimeout(requoteTimerRef.current);
          requoteTimerRef.current = setTimeout(() => {
            // Re-quote — only when a watched field changes.
            const newSnapshot = JSON.stringify(extractRequoteSnapshot(next));
            const shouldRequote = newSnapshot !== requoteSnapshotRef.current;
            if (!shouldRequote) return;
            requoteSnapshotRef.current = newSnapshot;

            fireNewQuote(sellerFreightProviderId);
          }, 1000);
        }
      }

      // Auto-save — only when editing an existing fulfillment plan (not a new quote).
      if (!fulfillmentPlanIdRef.current) return;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(async () => {
        const currentFulfillmentPlanId = fulfillmentPlanIdRef.current;
        const fp = getFulfillmentPlan();
        const payload = buildSaveConfigurationPayload(
          currentFulfillmentPlanId,
          formStateRef.current,
          selectedRateRef.current,
          ratesRef.current,
          mapWeightUnit(weightUnit),
          mapDimensionUnit(dimensionUnit),
          fp?.freightConfiguration?.freightFulfillmentPlanStatusID ?? 0,
          fp?.freightConfiguration?.freightNumber ?? null,
        );
        if (!payload) return;
        try {
          const result = await saveConfiguration(currentFulfillmentPlanId!, payload);
          onSaveConfigurationSuccess(result);
          if (!mountedRef.current) return;

          // Re-quote — only when a watched field changes.
          const newSnapshot = JSON.stringify(extractRequoteSnapshot(next));
          const shouldRequote = newSnapshot !== requoteSnapshotRef.current;

          if (shouldRequote && fulfillmentPlanIdForQuote) {
            requoteSnapshotRef.current = newSnapshot;
            fireFulfillmentPlanQuote(fulfillmentPlanIdForQuote);
          }
        } catch {
          // auto-save failures are silent — user can still manually Save Draft
        }
      }, 1000);
    },
    [
      mode,
      fulfillmentPlanIdRef,
      formStateRef,
      mountedRef,
      getConnectedProviderId,
      fireNewQuote,
      fireFulfillmentPlanQuote,
      getFulfillmentPlan,
      selectedRateRef,
      ratesRef,
      weightUnit,
      dimensionUnit,
      onSaveConfigurationSuccess,
    ],
  );

  const resetRequoteSnapshot = useCallback((state: FreightBookingFormState) => {
    requoteSnapshotRef.current = JSON.stringify(extractRequoteSnapshot(state));
  }, []);

  return { onChange, resetRequoteSnapshot, triggerRequote };
};
